import { omit } from "@mixitone/util";
import clsx from "clsx";
import { useEffect, useState } from "react";

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  checked: boolean;
  allowIndeterminate?: boolean;
  indeterminate?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  disabled?: boolean;
  small?: boolean; // New prop for size variant
  ref?: React.Ref<HTMLInputElement>;
}

export const Toggle: React.FC<Props> = ({ ref, ...props }) => {
  const [checked, setChecked] = useState(props.checked);

  const rest = omit(props, ["checked", "onChange", "small", "indeterminate", "allowIndeterminate"]);
  const error = props.error;
  const small = props.small;
  const readOnly = props.readOnly;
  const indeterminate = props.indeterminate;
  const allowIndeterminate = props.allowIndeterminate;

  useEffect(() => {
    if (props.checked === checked) return;
    setChecked(props.checked);
  }, [props.checked]);

  useEffect(() => {
    if (ref && "current" in ref && ref.current) {
      (ref.current as HTMLInputElement).indeterminate = !!(allowIndeterminate && indeterminate);
    }
  }, [indeterminate, allowIndeterminate, ref]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;

    if (allowIndeterminate) {
      if (indeterminate) {
        // If currently indeterminate, move to unchecked state
        e.target.checked = false;
        e.target.indeterminate = false;
      } else if (checked && !e.target.checked) {
        // If currently checked, move to indeterminate state
        e.target.checked = false;
        e.target.indeterminate = true;
      } else {
        // If currently unchecked, move to checked state
        e.target.checked = true;
        e.target.indeterminate = false;
      }
    }

    setChecked(e.target.checked);
    props.onChange(e);
  };

  return (
    // biome-ignore lint/a11y/useFocusableInteractive: meh
    <label
      className={clsx("relative inline-flex items-center", {
        "cursor-pointer": !props.disabled && !readOnly,
      })}
      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: meh
      // biome-ignore lint/a11y/useAriaPropsForRole: meh
      // biome-ignore lint/a11y/useSemanticElements: meh
      role="checkbox"
      aria-disabled={props.disabled}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={props.disabled}
        onChange={handleChange}
        {...rest}
        ref={ref}
      />
      <div
        className={clsx(
          "peer rounded-full",
          small ? "h-4 w-7" : "h-6 w-11", // Conditional sizing
          "after:absolute after:rounded-full after:border after:transition-all after:content-['']",
          small
            ? "after:left-[2px] after:top-[2px] after:h-3 after:w-3"
            : "after:left-[2px] after:top-[2px] after:h-5 after:w-5",
          "after:border-gray-400 after:bg-white",
          "peer-checked:bg-brand-600",
          "peer-checked:after:border-white",
          "dark:border-gray-600 dark:bg-gray-700",
          "peer-focus:outline-none",
          "peer-checked:peer-disabled:bg-gray-300 peer-checked:peer-disabled:after:border-gray-400",
          {
            "peer-checked:after:translate-x-full": !indeterminate,
            "after:translate-x-1/2": indeterminate,
            "bg-gray-400": !checked && !indeterminate,
            "bg-gray-300": !checked && indeterminate,
          },
        )}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </label>
  );
};
